import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { addMinutes, format } from "date-fns";
// PR-AV: Button + toast were referenced (lines 893, 909, 915) without
// imports — TS strict:false hid this. Added now.
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import { calculateBookingPrice, calculatePrivateBookingPerSeatPrice, type PricingRule, type SpecialPriceDate } from "@/lib/pricing";
import { calculateUniversalPrice, type PlayerPriceResult, type CategoryConfig } from "@/lib/playerPricing";
import { getMaxPlayersByCourtType } from "@/lib/courtUtils";
import { mergeConsecutiveSlots, type MergedBooking } from "@/utils/mergeSlots";
import { isZeroChargeBooking } from "@/lib/bookingChargeGate";
import type { PolicyTier } from "@/lib/cancellationPolicyLine";
// T1/G-001: open-match creates a REAL activity via the SAME facit-pinned builder +
// create_activity_v2 RPC as the activity wizard — facit now guards both call-sites.
import { buildWizardActivityPayload } from "@/lib/buildWizardActivityPayload";
import type { ActivityWizardFormData } from "@/components/activity/wizard/ActivityWizardContext";
import type { MultiSelectSlot } from "@/hooks/useMultiSelect";
import type { AddonSelection } from "./BookingAddonsStep";
import type { PaymentMethodOption } from "./paymentTypes";
import type { Enums } from "@/integrations/supabase/types";
import { useOrganization } from "@/hooks/useOrganization";
import { useActivityTypes } from "@/hooks/useActivityTypes";
import { useCustomerCategories } from "@/hooks/useCustomerCategories";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { useSavedCards } from "@/hooks/useSavedCards";
import { useTranslation } from "react-i18next";

import WizardHeader from "./wizard/WizardHeader";
import WizardFooter from "./wizard/WizardFooter";
import Step1Type from "./wizard/Step1Type";
import Step2Private from "./wizard/Step2Private";
import Step2Open from "./wizard/Step2Open";
import Step3Payment from "./wizard/Step3Payment";
import StepConfirmation from "./wizard/StepConfirmation";

// ── Types ──

interface PlayerProfile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  level: number | null;
  categorySlug?: string;
  categoryName?: string;
}

export interface CourtSelection {
  courtId: string;
  courtName: string;
  courtType?: Enums<"court_type">;
  maxPlayers?: number;
}

export interface BookingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courtName: string;
  courtId: string;
  startTime: Date;
  hallId: string;
  organizationId: string;
  pricingRules: PricingRule[];
  userId: string;
  minDuration: number;
  maxDuration: number;
  timeStep: number;
  maxAvailableMinutes: number;
  // T1/G-001: onConfirm is now PRIVATE-booking-only — open_match creates its
  // activity via create_activity_v2 inside the wizard (handleCreateOpenMatch)
  // and never calls onConfirm. isActivity + the never-populated activity?
  // object are removed (the dead direct-INSERT branch in bookingConfirmation
  // is deleted in the same commit series).
  onConfirm: (opts: {
    durationMinutes: number;
    maxPlayers: number;
    invitedPlayers: string[];
    splitStrategy: "full" | "per_person";
    paymentMethod: string;
    addons: AddonSelection[];
    playerPrices: PlayerPriceResult[];
    discountAmount?: number;
    discountCodeId?: string | null;
    bookerCategorySlug?: string; // Bug B fix: booker's resolved category for slot pricing
  }) => Promise<void>;
  specialDates?: SpecialPriceDate[];
  courtType?: Enums<"court_type">;
  bookerCategorySlug?: string;
  courts?: CourtSelection[];
  availableCourts?: Array<{ id: string; name: string; type?: Enums<"court_type"> }>;
  onAddCourt?: (courtId: string) => void;
  onRemoveCourt?: (courtId: string) => void;
  multiSelectSlots?: MultiSelectSlot[];
  onRemoveMultiSlot?: (index: number) => void;
  /**
   * @deprecated T1/G-105: dead prop — never consumed here (destructuring
   * removed). Field kept only so Book.tsx (T7-owned) still compiles; T7
   * removes the Book.tsx state + pass-through, then this field.
   */
  lastBookingId?: string | null;
}

type WizardStep = "type" | "details" | "payment" | "confirmation";
type BookingType = "private" | "open_match";

// PR-AV: removed dead AutoCreateOpenMatch component + AutoCreateProps interface.
// Was extracted but never wired into the wizard JSX; TS6133 flagged it after
// CI started running tsc --build. If reintroduced, see git history for the
// original implementation (relied on supabase.functions.invoke create-open-match).

export default function BookingWizard({
  open,
  onOpenChange,
  courtName,
  courtId,
  startTime,
  hallId,
  organizationId,
  pricingRules,
  userId,
  minDuration,
  maxDuration,
  timeStep,
  maxAvailableMinutes,
  onConfirm,
  specialDates = [],
  courtType,
  bookerCategorySlug = "standard",
  courts: courtsProp,
  availableCourts = [],
  onAddCourt,
  onRemoveCourt,
  multiSelectSlots,
  // onRemoveMultiSlot + lastBookingId intentionally not destructured —
  // types kept for caller API (lastBookingId is dead, see G-105 note above).
}: BookingWizardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("booking");
  const courts: CourtSelection[] = courtsProp ?? [{ courtId, courtName, courtType }];

  // ── Merged bookings for multi-select ──
  const mergedBookings: MergedBooking[] = useMemo(() => {
    if (!multiSelectSlots || multiSelectSlots.length <= 1) return [];
    return mergeConsecutiveSlots(
      multiSelectSlots.map((s) => ({
        courtId: s.courtId,
        courtName: s.courtName,
        startTime: s.startTime,
        endTime: s.endTime,
        price: s.price,
      }))
    );
  }, [multiSelectSlots]);

  const isMultiSlotMode = mergedBookings.length > 0;
  const allSameTime =
    isMultiSlotMode &&
    mergedBookings.every(
      (b) =>
        b.startTime === mergedBookings[0].startTime &&
        b.endTime === mergedBookings[0].endTime
    );
  const multiSlotTotalPrice = isMultiSlotMode
    ? mergedBookings.reduce((sum, b) => sum + b.totalPrice, 0)
    : 0;

  // ── Wizard state ──
  const [currentStep, setCurrentStep] = useState<WizardStep>("type");
  const [bookingType, setBookingType] = useState<BookingType | null>(null);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [booking, setBooking] = useState(false);
  const [_bookingComplete, setBookingComplete] = useState(false);
  const [pricesReady, setPricesReady] = useState(false);

  // Booking details state
  const [durationMinutes, setDurationMinutes] = useState(
    Math.min(60, maxAvailableMinutes)
  );
  const [invitedPlayers, setInvitedPlayers] = useState<PlayerProfile[]>([]);
  const [splitStrategy, setSplitStrategy] = useState<"full" | "per_person">(
    "full"
  );
  const [paymentMethod, setPaymentMethod] = useState<string>("stripe");
  const [addonSelections, setAddonSelections] = useState<AddonSelection[]>([]);
  const [playerPrices, setPlayerPrices] = useState<PlayerPriceResult[]>([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountCodeId, setDiscountCodeId] = useState<string | null>(null);

  const handleDiscountChange = useCallback((amount: number, codeId: string | null) => {
    setDiscountAmount(amount);
    setDiscountCodeId(codeId);
  }, []);

  // ── Hooks ──
  const { organization } = useOrganization();
  const { types: activityTypes } = useActivityTypes();
  const creatableTypes = activityTypes.filter((t) => t.player_can_create);
  const { categories } = useCustomerCategories(organization?.id);
  const { activeMethods: activePaymentMethods } = usePaymentMethods();
  const { cards: savedCards } = useSavedCards();

  // Credit balance — Sprint D dual-state: prefer balance_minor, fall back to legacy.
  const { data: creditData } = useQuery({
    queryKey: ["player-credits-booking", userId, organizationId],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_credits" as any)
        .select("balance, balance_minor, currency")
        .eq("user_id", userId)
        .eq("organization_id", organizationId)
        .maybeSingle();
      return data as { balance: number; balance_minor: number | null; currency: string } | null;
    },
    enabled: !!userId && !!organizationId,
  });
  // creditBalance returned in kronor (matches legacy comparison `creditBalance < priceInfo.total`).
  const _creditMoney = resolveMoney(creditData?.balance_minor, creditData?.balance, creditData?.currency);
  const creditBalance = _creditMoney ? Math.round(_creditMoney.valueMinor / 100) : 0;

  // ── Trusted customer check (can book without payment) ──
  const { data: isTrustedCustomer = false } = useQuery({
    queryKey: ["trusted-customer-check", userId, organizationId],
    queryFn: async () => {
      // Find customer record for this user+org, then check if any of their groups has can_book_without_payment
      const { data: customer } = await supabase
        .from("customers")
        .select("id")
        .eq("user_id", userId)
        .eq("organization_id", organizationId)
        .maybeSingle();
      if (!customer) return false;
      const { data: groupRows } = await supabase
        .from("customer_group_members" as any)
        .select("customer_groups!inner(can_book_without_payment)")
        .eq("customer_id", customer.id);
      if (!groupRows || groupRows.length === 0) return false;
      return groupRows.some((r: any) => r.customer_groups?.can_book_without_payment === true);
    },
    enabled: !!userId && !!organizationId,
  });

  // ── Active booking count (for max bookings pre-check) ──
  const { data: activeBookingCount } = useQuery({
    queryKey: ["active-booking-count", userId],
    queryFn: async () => {
      const { count } = await supabase
        .from("bookings")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("status", "confirmed")
        .gt("start_time", new Date().toISOString());
      return count ?? 0;
    },
    enabled: !!userId && currentStep === "payment",
  });

  const isMember = useMemo(() => {
    const slug = bookerCategorySlug?.toLowerCase();
    return slug === "member" || slug === "medlem";
  }, [bookerCategorySlug]);

  const maxConcurrentBookings = isMember
    ? (organization?.member_max_concurrent_bookings ?? 10)
    : (organization?.max_concurrent_bookings ?? 3);

  const isAtBookingLimit =
    activeBookingCount != null && activeBookingCount >= maxConcurrentBookings;

  // ── Derived values ──
  const totalMaxPlayers = useMemo(
    () =>
      courts.reduce(
        (sum, c) => sum + (c.maxPlayers ?? getMaxPlayersByCourtType(c.courtType)),
        0
      ),
    [courts]
  );

  const effectiveMax = Math.min(maxDuration, maxAvailableMinutes);
  const effectiveMin = Math.max(minDuration, timeStep);

  useEffect(() => {
    setDurationMinutes(Math.min(60, effectiveMax));
  }, [startTime, effectiveMax]);

  const endTime = useMemo(
    () => addMinutes(startTime, durationMinutes),
    [startTime, durationMinutes]
  );

  const durationSteps = useMemo(() => {
    const steps: number[] = [];
    for (let d = effectiveMin; d <= effectiveMax; d += timeStep) {
      steps.push(d);
    }
    return steps;
  }, [effectiveMin, effectiveMax, timeStep]);

  // ── Price calculation ──
  //
  // Bug B fix #3 — PER-SEAT pricing for PRIVATE bookings (operator-locked):
  //   booker_seat (at booker_category) + invited_seats (each at their cat)
  //   + empty_seats (at standard). See calculatePrivateBookingPerSeatPrice
  //   in src/lib/pricing.ts. For 1 member booker + 0 invited + 4-seat / 490 kr:
  //   0 + 0 + 3 × 122.50 = 367.50 kr (the operator-confirmed result).
  //
  // OPEN-MATCH path is UNCHANGED (F5): pricePerCourt returns the standard
  // court price as the reference (base_court_price_minor stays at 49000);
  // per-player shares are computed asynchronously via playerPricing.ts and
  // surfaced as `playerCost` for the booker (member → 0).
  //
  // MULTI-COURT for PRIVATE: each court is per-seat independently. The
  // booker is treated as occupying ONE seat in EACH court (a private
  // booking on N courts means the booker reserves N courts; this mirrors
  // the pre-fix behavior where each court contributed its own standard
  // total). Invited players also count per-court. Standard-booker no-op
  // holds per-court via the all-standard guard.
  const pricePerCourt = useMemo(() => {
    const isOpenMatch = bookingType === "open_match";

    return courts.map((court) => {
      const stdPrice = calculateBookingPrice(
        pricingRules,
        hallId,
        startTime,
        durationMinutes,
        "standard",
        specialDates,
      );

      // OPEN-MATCH: return standard reference unchanged (F5 — preserves
      // base_court_price_minor and per-joiner share-calc semantics).
      // `totalMinor` läggs på enbart för att hålla returtypen enhetlig; open
      // match når aldrig betalsteget (T1/G-001 — CTA:n skapar aktiviteten vid
      // details), så 0 kr-gaten läser den aldrig.
      if (isOpenMatch) return { ...stdPrice, totalMinor: Math.round(stdPrice.total * 100) };

      // PRIVATE: per-seat formula
      const seatCount =
        court.maxPlayers ?? getMaxPlayersByCourtType(court.courtType);
      const courtPriceMinorFor = (cat: string) =>
        Math.round(
          calculateBookingPrice(
            pricingRules,
            hallId,
            startTime,
            durationMinutes,
            cat,
            specialDates,
          ).total * 100,
        );

      const perSeat = calculatePrivateBookingPerSeatPrice({
        seatCount,
        bookerCategorySlug: bookerCategorySlug ?? "standard",
        invitedCategorySlugs: invitedPlayers.map(
          (p) => p.categorySlug ?? "standard",
        ),
        courtPriceMinorForCategory: courtPriceMinorFor,
      });

      // Return shape compatible with calculateBookingPrice consumers:
      // total in integer kronor (Math.round on öre → kr). Precision is
      // preserved in the INSERT path via explicit price_total_minor.
      return {
        ...stdPrice,
        total: Math.round(perSeat.totalMinor / 100),
        // 0 kr-gaten (L60) måste jämföra i ÖRE — `total` ovan avrundar, så
        // 49 öre blir 0 kr och en kr-gate hade gömt betalsektionen på en
        // bokning som faktiskt debiterar. Charge-vägen jämför likaså i öre
        // (bookingConfirmation.ts: effectivePriceMinor).
        totalMinor: perSeat.totalMinor,
      };
    });
  }, [
    pricingRules,
    hallId,
    startTime,
    durationMinutes,
    specialDates,
    courts,
    bookerCategorySlug,
    invitedPlayers,
    bookingType, // Bug B fix #2: recompute when user toggles private ↔ open-match
  ]);

  const price = useMemo(
    () => ({
      total: pricePerCourt.reduce((sum, p) => sum + p.total, 0),
    }),
    [pricePerCourt]
  );

  const totalPrice = isMultiSlotMode ? multiSlotTotalPrice : price.total;

  // ── 0 kr-gaten (L60) ──────────────────────────────────────────────────
  // Hänger på CHARGE-villkoret, inte på `totalPrice`. Charge-vägen
  // (bookingConfirmation.ts) jämför `effectivePriceMinor` i ÖRE och PER SLOT;
  // gaten gör exakt detsamma via bookingChargeGate.
  //
  // Multi-slot-grenen har bara kronor att erbjuda (MergedBooking.totalPrice
  // kommer från griden, som redan räknat i kr) — den befintliga precisionen
  // går inte att återskapa här, så den konverteras rakt av. Enkel-/multi-court
  // använder öret från calculatePrivateBookingPerSeatPrice, samma helper som
  // charge-vägen når via resolveBookingTargetPrice.
  const slotTotalsMinor = useMemo(
    () =>
      isMultiSlotMode
        ? mergedBookings.map((b) => Math.round(b.totalPrice * 100))
        : pricePerCourt.map((p) => p.totalMinor),
    [isMultiSlotMode, mergedBookings, pricePerCourt],
  );
  const isZeroCharge = useMemo(
    () => isZeroChargeBooking({ slotTotalsMinor, discountAmount }),
    [slotTotalsMinor, discountAmount],
  );

  // ── Avbokningsvillkor för policyraden (per hall, aldrig hårdkodat) ──
  const cancellationSettings = (organization?.settings as
    | { cancellation?: { tiers?: PolicyTier[]; no_show?: { enabled?: boolean } } }
    | null
    | undefined)?.cancellation;
  const cancellationTiers = cancellationSettings?.tiers ?? null;
  const noShowEnabled = cancellationSettings?.no_show?.enabled === true;

  // ── Payment methods (with saved cards) ──
  const availableMethods = useMemo(() => {
    const methods: PaymentMethodOption[] = [];

    // 1. Saved cards first (default card first)
    const sortedCards = [...savedCards].sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
    for (const card of sortedCards) {
      methods.push({
        value: `saved_card:${card.id}`,
        label: `${card.card_brand.charAt(0).toUpperCase() + card.card_brand.slice(1)} •••• ${card.card_last4}`,
        icon: "CreditCard",
        type: "online",
        enabled: true,
        available: true,
        detail: card.is_default ? t("common:profile.payment.default") : undefined,
      });
    }

    // 2. Regular payment methods
    const onlineKeys = new Set(["stripe", "credits"]);
    for (const m of activePaymentMethods) {
      const isCredits = m.key === "credits";
      const hasBalance = creditBalance > 0;
      methods.push({
        value: m.key,
        label: m.key === "stripe" && savedCards.length > 0 ? t("common:booking.payment.newCard") : m.name,
        icon: m.icon ?? "",
        type: onlineKeys.has(m.key) ? "online" : "offline",
        enabled: true,
        available: isCredits ? hasBalance : true,
        detail: isCredits && hasBalance
          ? `${t("balance")}: ${formatPrice(creditBalance)}`
          : undefined,
      });
    }

    return methods;
  }, [activePaymentMethods, creditBalance, savedCards, t]);

  // Filter methods based on require-payment setting + trusted customer status
  const filteredMethods = useMemo(() => {
    const requirePayment = (organization?.settings as any)?.booking_require_payment ?? true;
    if (!requirePayment) return availableMethods;
    if (isTrustedCustomer) return availableMethods;
    // Untrusted + require payment → only stripe, credits, and saved cards
    return availableMethods.filter(m =>
      ['stripe', 'credits'].includes(m.value) || m.value.startsWith('saved_card:')
    );
  }, [availableMethods, organization, isTrustedCustomer]);

  // Auto-select first available method
  useEffect(() => {
    const first = filteredMethods.find((m) => m.available);
    if (
      first &&
      !filteredMethods.find((m) => m.value === paymentMethod && m.available)
    ) {
      setPaymentMethod(first.value);
    }
  }, [filteredMethods, paymentMethod]);

  // ── Per-player price calculation (async, with cancellation) ──
  useEffect(() => {
    if (
      !userId ||
      !hallId ||
      !organization?.id ||
      categories.length === 0
    )
      return;

    // Mark stale immediately when deps change
    setPricesReady(false);
    setPricesLoading(true);
    let cancelled = false;

    const allPlayers = [userId, ...invitedPlayers.map((p) => p.id)];
    const totalPlayers = allPlayers.length;

    const catConfig: CategoryConfig[] = categories.map((c) => ({
      slug: c.slug,
      name: c.name,
      daily_minutes_limit: c.daily_minutes_limit ?? null,
      daily_limit_fallback_category:
        c.daily_limit_fallback_category ?? "standard",
      overflow_percent: c.overflow_percent ?? 100,
    }));

    // Open match: split court cost among all spots (not just current players)
    const courtSplitPlayers = bookingType === "open_match" ? totalMaxPlayers : totalPlayers;
    const activity = {
      courtCostTotal: totalPrice,
      courtParticipants: courtSplitPlayers,
      courtDurationMinutes: durationMinutes,
      serviceCostTotal: 0,
      serviceParticipants: courtSplitPlayers,
    };

    Promise.all(
      allPlayers.map((pId) =>
        calculateUniversalPrice(
          pId,
          organization.id,
          hallId,
          startTime,
          activity,
          pricingRules,
          specialDates,
          catConfig
        )
      )
    )
      .then((results) => {
        if (cancelled) return;
        setPlayerPrices(results);
        setPricesLoading(false);
        setPricesReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setPricesLoading(false);
        setPricesReady(true);
      });

    return () => { cancelled = true; };
  }, [
    userId,
    invitedPlayers,
    durationMinutes,
    startTime,
    hallId,
    organization?.id,
    pricingRules,
    specialDates,
    categories,
    totalPrice,
    bookingType,
    totalMaxPlayers,
  ]);

  // ── Addons total ──
  const addonsTotal = useMemo(
    () => addonSelections.reduce((sum, s) => sum + s.price * s.quantity, 0),
    [addonSelections]
  );

  // ── Player cost (what the current user pays) ──
  // Returns null when async price calc is in progress (for open match)
  const playerCost = useMemo((): number | null => {
    const grossAmount = totalPrice + addonsTotal;

    if (!bookingType || bookingType === "open_match") {
      // Open match: wait for async category-aware price
      if (!pricesReady || playerPrices.length === 0) return null;
      const perPlayer = playerPrices[0].courtShare;
      const cappedDiscount = Math.min(discountAmount, perPlayer);
      const raw = Math.max(0, perPlayer - cappedDiscount);
      if (raw === 0) return 0;
      return Math.max(raw, 3); // Stripe min 3 SEK
    }

    const payFullAmount = Math.max(0, grossAmount - discountAmount);
    const pricePerPerson = Math.ceil(payFullAmount / totalMaxPlayers);
    const confirmedPlayers = playerPrices.length;
    const emptySpots = Math.max(0, totalMaxPlayers - confirmedPlayers);

    if (splitStrategy === "full") {
      return payFullAmount;
    }

    // per_person: booker pays own share + empty spots
    return pricePerPerson + pricePerPerson * emptySpots;
  }, [
    totalPrice,
    addonsTotal,
    discountAmount,
    totalMaxPlayers,
    playerPrices,
    pricesReady,
    bookingType,
    splitStrategy,
  ]);

  // ── Merged bookings for header display ──
  const headerBookings: MergedBooking[] = useMemo(() => {
    if (isMultiSlotMode) return mergedBookings;
    return [
      {
        courtId,
        courtName: courts.length === 1 ? courts[0].courtName : t("nCourts", { count: courts.length }),
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        totalPrice: totalPrice,
        slotCount: 1,
        originalStartTimes: [startTime.toISOString()],
      },
    ];
  }, [
    isMultiSlotMode,
    mergedBookings,
    courtId,
    courts,
    startTime,
    endTime,
    totalPrice,
  ]);

  // Activity min price.
  // Sprint D PR-AB Phase 4-aware: prefer default_price_per_person_minor over legacy.
  const activityMinPrice = useMemo(() => {
    if (creatableTypes.length === 0) return null;
    const prices = creatableTypes.map((t) => {
      const minor = (t as any).default_price_per_person_minor;
      return minor != null ? minor / 100 : (t.default_price_per_person ?? 0);
    });
    const min = Math.min(...prices);
    return min > 0 ? min : null;
  }, [creatableTypes]);

  // ── Navigation ──
  const goToStep = useCallback(
    (step: WizardStep, dir: "forward" | "back" = "forward") => {
      setDirection(dir);
      setCurrentStep(step);
    },
    []
  );

  const handleSelectType = useCallback(
    (type: "private" | "open_match" | "activity") => {
      if (type === "activity") {
        // Close wizard and navigate to create form with court/time state
        onOpenChange(false);

        // Compute correct start/end from multi-select slots or single booking
        let navStartTime: string;
        let navEndTime: string;
        if (isMultiSlotMode && mergedBookings.length > 0) {
          const earliest = mergedBookings.reduce((min, b) =>
            b.startTime < min ? b.startTime : min, mergedBookings[0].startTime);
          const latest = mergedBookings.reduce((max, b) =>
            b.endTime > max ? b.endTime : max, mergedBookings[0].endTime);
          navStartTime = earliest;
          navEndTime = latest;
        } else {
          navStartTime = startTime.toISOString();
          navEndTime = endTime.toISOString();
        }

        // Merge courts prop + multiSelectSlots for all unique courts
        const allCourtIds = [...new Set([
          ...courts.map(c => c.courtId),
          ...(multiSelectSlots ?? []).map((s: any) => s.courtId),
        ])];
        const allCourtNames = [...new Set([
          ...courts.map(c => c.courtName),
          ...(multiSelectSlots ?? []).map((s: any) => s.courtName),
        ])];

        navigate("/create-match", {
          state: {
            courtIds: allCourtIds,
            courtNames: allCourtNames,
            startTime: navStartTime,
            endTime: navEndTime,
            selectedSlots: multiSelectSlots ?? [],
          },
        });
        return;
      }
      setBookingType(type as BookingType);
      setSplitStrategy("full");
      goToStep("details", "forward");
    },
    [goToStep, navigate, onOpenChange, courts, isMultiSlotMode, mergedBookings, startTime, endTime, multiSelectSlots]
  );

  const goBack = useCallback(() => {
    if (currentStep === "details") {
      setBookingType(null);
      goToStep("type", "back");
    } else if (currentStep === "payment") {
      setDiscountAmount(0);
      setDiscountCodeId(null);
      goToStep("details", "back");
    }
  }, [currentStep, goToStep]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    // Reset state after close
    setTimeout(() => {
      setCurrentStep("type");
      setBookingType(null);
      setInvitedPlayers([]);
      setSplitStrategy("full");
      setAddonSelections([]);
      setBookingComplete(false);
    }, 300);
  }, [onOpenChange]);

  // ── Confirm booking (PRIVATE only — open_match creates via RPC below) ──
  const handleConfirm = async () => {
    if (bookingType !== "private") return;
    setBooking(true);
    try {
      await onConfirm({
        durationMinutes,
        maxPlayers: totalMaxPlayers,
        invitedPlayers: invitedPlayers.map((p) => p.id),
        splitStrategy,
        paymentMethod,
        addons: addonSelections,
        playerPrices,
        discountAmount,
        discountCodeId,
        bookerCategorySlug, // Bug B fix: pass through booker's resolved category for slot pricing
      });
      setBookingComplete(true);
      goToStep("confirmation", "forward");
    } finally {
      setBooking(false);
    }
  };

  // ── T1/G-001: create open match as a REAL activity via create_activity_v2 ──
  //
  // Same facit-pinned builder + atomic RPC as the activity wizard (the RPC
  // creates the court bookings itself and does NOT auto-join the creator).
  // Per decision 9 the organizer is NOT charged at create — they land on
  // /activities/:id and join+pay there like anyone else. This REPLACES the
  // old "pay your share at create" model.
  const handleCreateOpenMatch = async () => {
    if (bookingType !== "open_match" || booking) return;
    // Type resolution (locked decision A): the org's 'social' type is the
    // open-match anchor (slug is not editable in admin UI — display name is);
    // fallback: first player-creatable non-tournament type by sort_order.
    // activityTypes is already active-only + sort_order-sorted.
    const resolvedType =
      activityTypes.find((at) => at.slug === "social") ??
      activityTypes.find((at) => at.player_can_create && !at.tournament_format);
    if (!resolvedType) {
      // Operator addition 1: clear error, no silent failure.
      toast.error(t("openMatchTypeMissing"));
      return;
    }
    setBooking(true);
    try {
      const courtIds = courts.map((c) => c.courtId);
      const autoTitle = t("openMatchAutoTitle", {
        court: courts.length === 1 ? courts[0].courtName : t("nCourts", { count: courts.length }),
        time: format(startTime, "HH:mm"),
      });
      const fd: ActivityWizardFormData = {
        activityTypeId: resolvedType.id,
        sport_type: "padel",
        visibility: "public",
        date: format(startTime, "yyyy-MM-dd"),
        durationMinutes,
        startTime: format(startTime, "HH:mm"),
        endTime: format(endTime, "HH:mm"),
        spotsTotal: totalMaxPlayers,
        minLevel: 1,
        maxLevel: 10,
        activityTitle: autoTitle,
        // Money defaults (locked): organizer fee 0, system mode, member
        // discount on — participants pay their share at JOIN via join_activity_v2.
        organizerFeeMinor: 0,
        paymentMode: "system",
        applyMemberDiscount: true,
        invitedUserIds: invitedPlayers.map((p) => p.id),
      };
      const { data: activityId, error } = await (supabase.rpc as any)(
        "create_activity_v2",
        buildWizardActivityPayload(fd, {
          organizationId,
          selectedType: { slug: resolvedType.slug, tournament_format: resolvedType.tournament_format },
          courtIds,
          spotsTotal: totalMaxPlayers,
        }),
      );
      if (error) {
        const msg = error.message ?? "";
        if (/overlap|reserved|blocked/i.test(msg)) {
          toast.error(t("openMatchCourtTaken"));
        } else {
          toast.error(msg || t("openMatchCreateFailed"));
        }
        return;
      }
      // Locked decision B: preserve invited players via the activity wizard's
      // post-create invitation flow — error-isolated so a failed invite never
      // blocks create or navigation (rows enable /invite/:token even if the
      // fire-and-forget email fn is absent).
      const invitedUserIds = fd.invitedUserIds ?? [];
      if (activityId && invitedUserIds.length > 0) {
        try {
          await (supabase.rpc as any)("create_activity_invitations", {
            p_activity_id: activityId,
            p_user_ids: invitedUserIds,
            p_emails: [],
          });
          void supabase.functions
            .invoke("send-activity-invite", { body: { activity_id: activityId } })
            .catch(() => {});
        } catch (inviteErr) {
          console.error("[T1 open-match invite] create_activity_invitations failed:", inviteErr);
          toast.error(t("openMatchInviteFailed"));
        }
      }
      toast.success(t("openMatchCreated"));
      onOpenChange(false);
      // Decision 5 (T1v2): land on the LOGGED-IN detail view where the
      // organizer can join+pay — not the anonymous /aktiviteter view.
      navigate(activityId ? `/activities/${activityId}` : "/play");
    } catch (e) {
      const m = e instanceof Error ? e.message : "";
      toast.error(m || t("openMatchCreateFailed"));
    } finally {
      setBooking(false);
    }
  };

  // ── Action label for footer ──
  const actionLabel = useMemo(() => {
    // T1/G-001: open_match ends at details — the CTA creates the activity
    // (no payment step; organizer pays at join per decision 9).
    if (currentStep === "details" && bookingType === "open_match") return t("createOpenMatch");
    if (currentStep === "details") return t("nextPayment");
    // 0 kr → ingen betalning sker, så CTA:n ska inte lova en.
    if (currentStep === "payment") return isZeroCharge ? t("bookNoPayment") : t("bookAndPay");
    return "";
  }, [currentStep, bookingType, isZeroCharge, t]);

  const handleFooterAction = () => {
    if (currentStep === "details" && bookingType === "open_match") {
      void handleCreateOpenMatch();
    } else if (currentStep === "details") {
      goToStep("payment", "forward");
    } else if (currentStep === "payment") {
      handleConfirm();
    }
  };

  // ── Prevent background scroll ──
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 sm:block"
        onClick={handleClose}
      />

      {/* Wizard container — side panel on desktop, full-screen on mobile */}
      <div
        className={cn(
          "fixed inset-0 z-[60] livfull-wizard-bg flex flex-col",
          "sm:relative sm:inset-auto sm:max-w-[480px] sm:mx-auto sm:mt-[5vh]",
          "sm:rounded-xl sm:shadow-xl sm:max-h-[90vh]",
          "lg:fixed lg:inset-y-0 lg:right-0 lg:left-auto lg:w-[420px] lg:max-w-none lg:m-0 lg:rounded-none lg:rounded-l-xl lg:max-h-none"
        )}
      >
        <WizardHeader
          step={currentStep}
          onBack={goBack}
          onClose={handleClose}
          mergedBookings={headerBookings}
          bookingType={bookingType}
          selectedDate={startTime}
          totalSteps={3}
        />

        {/* Step content with transitions */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <div
            key={currentStep + (bookingType ?? "")}
            className={cn(
              "animate-in fade-in duration-200",
              direction === "forward" ? "slide-in-from-right-4" : "slide-in-from-left-4"
            )}
          >
            {currentStep === "type" && (
              <Step1Type
                totalPrice={totalPrice}
                totalMaxPlayers={totalMaxPlayers}
                isMultiSelect={isMultiSlotMode}
                allSameTime={allSameTime}
                hasCreatableActivityTypes={creatableTypes.length > 0}
                activityMinPrice={activityMinPrice}
                onSelectType={handleSelectType}
                courtCount={courts.length}
              />
            )}

            {currentStep === "details" && bookingType === "private" && (
              <Step2Private
                startTime={startTime}
                durationMinutes={durationMinutes}
                onDurationChange={setDurationMinutes}
                durationSteps={durationSteps}
                isMultiSlotMode={isMultiSlotMode}
                userId={userId}
                organizationId={organizationId}
                invitedPlayers={invitedPlayers}
                onInvitedPlayersChange={setInvitedPlayers}
                splitStrategy={splitStrategy}
                onSplitChange={setSplitStrategy}
                totalPrice={totalPrice}
                courtMaxPlayers={totalMaxPlayers}
                playerPrices={playerPrices}
                addonsTotal={addonsTotal}
                courts={courts}
                availableCourts={availableCourts}
                onAddCourt={onAddCourt}
                onRemoveCourt={onRemoveCourt}
                addonSelections={addonSelections}
                onAddonSelectionsChange={setAddonSelections}
              />
            )}

            {currentStep === "details" && bookingType === "open_match" && (
              <Step2Open
                startTime={startTime}
                durationMinutes={durationMinutes}
                onDurationChange={setDurationMinutes}
                durationSteps={durationSteps}
                isMultiSlotMode={isMultiSlotMode}
                userId={userId}
                organizationId={organizationId}
                invitedPlayers={invitedPlayers}
                onInvitedPlayersChange={setInvitedPlayers}
                totalPrice={totalPrice}
                totalMaxPlayers={totalMaxPlayers}
                playerPrices={playerPrices}
                pricesReady={pricesReady}
                totalCourts={courts.length}
              />
            )}

            {currentStep === "payment" && bookingType && (
              <Step3Payment
                bookingType={bookingType}
                startTime={startTime}
                endTime={endTime}
                durationMinutes={durationMinutes}
                courts={courts}
                invitedPlayers={invitedPlayers}
                splitStrategy={splitStrategy}
                totalPrice={totalPrice}
                courtMaxPlayers={totalMaxPlayers}
                playerPrices={playerPrices}
                addonsTotal={addonsTotal}
                currentUserId={userId}
                paymentMethod={paymentMethod}
                onPaymentMethodChange={setPaymentMethod}
                availableMethods={filteredMethods}
                creditBalance={creditBalance}
                organizationId={organizationId}
                discountAmount={discountAmount}
                onDiscountChange={handleDiscountChange}
                bookingLimitReached={isAtBookingLimit}
                activeBookingCount={activeBookingCount ?? 0}
                maxBookings={maxConcurrentBookings}
                isZeroCharge={isZeroCharge}
                cancellationTiers={cancellationTiers}
                noShowEnabled={noShowEnabled}
              />
            )}

            {currentStep === "confirmation" && bookingType && (
              <>
                <StepConfirmation
                  bookingType={bookingType}
                  courtName={
                    courts.length === 1
                      ? courts[0].courtName
                      : t("nCourts", { count: courts.length })
                  }
                  startTime={startTime}
                  endTime={endTime}
                  onClose={handleClose}
                  orgSettings={organization?.settings as Record<string, unknown> | null}
                />
                {/* Spår 1b: "Gör om till öppen match" post-booking-knapp
                    rivet med V2. V1-aktiviteter skapas via /create-match
                    eller admin-schedule, inte från privatbokning-flowet. */}
              </>
            )}
          </div>
        </div>

        <WizardFooter
          step={currentStep}
          playerCost={playerCost}
          totalCost={bookingType === "open_match" ? (playerCost ?? 0) : Math.max(0, totalPrice + addonsTotal - discountAmount)}
          actionLabel={actionLabel}
          onAction={handleFooterAction}
          isLoading={booking}
          disabled={pricesLoading || (currentStep === "payment" && isAtBookingLimit)}
        />
      </div>
    </div>
  );
}
